// Tracks whether a combat event has fired within the last 5 seconds.
// Latches true on any unit:combat / city:damaged / city:captured event,
// then falls back to false after the timeout. Mirrors the useSounds
// lastCount pattern so batched events aren't skipped.

import { useEffect, useRef } from 'react';

type SimEvent = { id?: number; type: string; payload?: Record<string, unknown> };

const COMBAT_EVENT_TYPES = new Set(['unit:combat', 'city:damaged', 'city:captured']);

const COMBAT_LATCH_MS = 5000;

export function useRecentCombat(events: ReadonlyArray<SimEvent> | undefined) {
  const recentCombatRef = useRef(false);
  const lastIdRef = useRef(-1);

  useEffect(() => {
    const list = events ?? [];
    if (list.length === 0) {
      lastIdRef.current = -1;
      return;
    }
    // Id watermark (events are tagged with monotonic ids and the history is
    // capped — see bootSim). A last id below the watermark means the match
    // reset: re-scan everything.
    const firstWm = typeof list[0]!.id === 'number' ? list[0]!.id! : 0;
    const lastWm =
      typeof list[list.length - 1]!.id === 'number'
        ? (list[list.length - 1]!.id as number)
        : list.length - 1;
    if (lastWm <= lastIdRef.current) return;
    const startWm = firstWm > lastIdRef.current + 1 ? firstWm : lastIdRef.current + 1;
    lastIdRef.current = lastWm;
    const newEvents = list.filter((e) => {
      const wm = typeof e.id === 'number' ? e.id : list.indexOf(e);
      return wm >= startWm;
    });
    const hadCombat = newEvents.some((e) => COMBAT_EVENT_TYPES.has(e.type));
    if (!hadCombat) return;
    recentCombatRef.current = true;
    const t = setTimeout(() => {
      recentCombatRef.current = false;
    }, COMBAT_LATCH_MS);
    return () => clearTimeout(t);
  }, [events]);

  return recentCombatRef;
}
